/**
 * The platform in the web app: API keys and the API reference, outbound webhooks, Discord and Slack
 * notification channels (System section), addons and
 * languages (admin, System section), and the client's language picker. Clients get their own API keys
 * and webhooks under Account, and the admin client page an "API" tab with them.
 */
import { lazy, Suspense } from 'react';

import { Loading } from '../../components/ui.jsx';

const page = (load, name) => () => load().then((m) => ({ Component: m[name] }));
const keys = () => import('./ApiKeys.jsx');
const webhooks = () => import('./Webhooks.jsx');
const docs = () => import('./ApiDocs.jsx');
const addons = () => import('./Addons.jsx');
const languages = () => import('./Languages.jsx');
const channels = () => import('./NotificationChannels.jsx');
const clientApi = () => import('./ClientApi.jsx');

const deferred = (load, name) => {
  const Lazy = lazy(() => load().then((m) => ({ default: m[name] })));
  return function Deferred(props) {
    return (
      <Suspense fallback={<Loading />}>
        <Lazy {...props} />
      </Suspense>
    );
  };
};

export default {
  name: 'platform',
  clientRoutes: [
    { path: 'account/language', lazy: page(languages, 'ClientLanguage') },
    { path: 'account/api-keys', lazy: page(clientApi, 'ClientApiKeys') },
    { path: 'account/webhooks', lazy: page(clientApi, 'ClientWebhooks') },
  ],
  adminRoutes: [
    { path: 'api-keys', lazy: page(keys, 'ApiKeys') },
    { path: 'api-docs', lazy: page(docs, 'ApiDocs') },
    { path: 'webhooks', lazy: page(webhooks, 'Webhooks') },
    { path: 'webhooks/:uuid', lazy: page(webhooks, 'WebhookDetail') },
    { path: 'notifications', lazy: page(channels, 'NotificationChannels') },
    { path: 'addons', lazy: page(addons, 'Addons') },
    { path: 'languages', lazy: page(languages, 'Languages') },
    { path: 'languages/:code', lazy: page(languages, 'LanguageEditor') },
  ],
  clientNav: [
    { key: 'language', to: '/account/language', order: 95, auth: true },
    { key: 'api-keys', to: '/account/api-keys', label: 'API keys', order: 96, auth: true },
    { key: 'webhooks', to: '/account/webhooks', label: 'Webhooks', order: 97, auth: true },
  ],
  adminClientTabs: [
    {
      key: 'api',
      label: 'API',
      order: 89,
      permission: 'admin.clients.read',
      Component: deferred(() => import('./ClientApiTab.jsx'), 'ClientApiTab'),
    },
  ],
  adminNav: [
    {
      section: 'integrations',
      to: '/admin/api-keys',
      label: 'API keys',
      permission: 'admin.api_keys.manage',
      order: 70,
    },
    {
      section: 'integrations',
      to: '/admin/api-docs',
      label: 'API reference',
      permission: 'admin.api_keys.manage',
      order: 71,
    },
    {
      section: 'integrations',
      to: '/admin/webhooks',
      label: 'Webhooks',
      permission: 'admin.webhooks.manage',
      order: 72,
    },
    { section: 'integrations', to: '/admin/addons', label: 'Addons', permission: 'admin.addons.manage', order: 80 },
    {
      section: 'system',
      to: '/admin/notifications',
      label: 'Notifications',
      permission: 'admin.notifications.manage',
      order: 73,
    },
    {
      section: 'system',
      to: '/admin/languages',
      label: 'Languages',
      permission: 'admin.settings.localization.read',
      order: 85,
    },
  ],
};
